import React, { useContext, useEffect, useState } from 'react';
import {
	Checkbox,
	Head,
	HintMessage,
	Wrapper,
	ContentWrapper,
	VignetteRadioButtons,
	IHelpContent,
	VignetteFuelType,
	getHintContent,
	checkHintContent,
	Drawer,
	getDrawerTitle,
	ArrowRight,
	IconButton,
	getCountryName,
} from '@adac-mp/components';
import { ITypeVignetteOrderingProcessFields, IVignetteOffer, VignetteContext } from '@adac-mp/shared';
import { withTracking } from '@adac-mp/components/src/utils/tracking/tracking';
import { ContentfulDrawerContent } from '@adac-mp/components/src/components/ContentfulDrawerContent';

interface ISelectionProps {
	hasError?: boolean;
	helpContent?: IHelpContent;
	supplierHasMultipleProducts?: boolean;
	supplierId: number;
	withFuelOptions?: boolean;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

const SelectionComponent = ({
	hasError,
	helpContent,
	supplierHasMultipleProducts,
	supplierId,
	withFuelOptions = false,
	updateValidatedStepsMap = () => {},
}: ISelectionProps) => {
	const { selectedProducts, contentfulLabels, updateSelectedProducts, vehicleCategory } = useContext(VignetteContext);
	const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const [hasDifferentFuel, setHasDifferentFuel] = useState<boolean>(
		selectedProducts.some(({ fuelTypeId }) => fuelTypeId !== -1),
	);
	const [fuel, setFuel] = useState(
		selectedProducts.reduce((_, curr) => (curr.fuelTypeId === -1 ? -1 : curr.fuelTypeId), -1),
	);
	const [teaserFuelPrice, setTeaserFuelPrice] = useState<number | undefined>(undefined);
	const onVignetteChanged = (vignette: IVignetteOffer, multiSelect = false) => {
		updateSelectedProducts(vignette, multiSelect);
	};
	const vignetteWithFuelUpdated = () => {
		if (hasDifferentFuel && fuel === -1) {
			updateValidatedStepsMap(false);
			return;
		}
		const isValid = selectedProducts.some(({ supplierId: currSupplierId }) => currSupplierId === supplierId);
		updateValidatedStepsMap(isValid);
	};

	useEffect(() => {
		if (withFuelOptions) {
			vignetteWithFuelUpdated();
		}
	}, [hasDifferentFuel, withFuelOptions, fuel, selectedProducts]);

	const onFuelChanged = (fuelId: number) => {
		setFuel(fuelId);
	};

	const defaultVignetteIdSelector = () => {
		const code = selectedProducts.find(
			({ supplierId: currSupplierId }) => currSupplierId === supplierId,
		)?.serviceCatalogCode;
		if (code) return code.charAt(code.length - 1) === 'N' ? code : code.concat('N');
		return;
	};

	const isSchweizPkwSelected = supplierId === 14 && vehicleCategory?.id === 2;
	const isSchweizMotorradSelected = supplierId === 14 && vehicleCategory?.id === 3;

	const renderFuelOptions = () => (
		<>
			<Checkbox
				data-cy="checkbox-fuel"
				size="lg"
				title="Dieses Fahrzeug wird mit Erdgas oder Biogas angetrieben."
				className="pl-4 pt-5 pb-4 items-center"
				label="Dieses Fahrzeug wird mit Erdgas oder Biogas angetrieben."
				checked={hasDifferentFuel}
				setChecked={(checked) => {
					if (!checked) {
						setFuel(-1);
					}
					// Special case to show either Erdgas or BioGas as teaser price
					setTeaserFuelPrice(checked ? 6 : undefined);
					setHasDifferentFuel(checked);
				}}
			/>
			{hasDifferentFuel && <VignetteFuelType indented className="pb-4" onFuelChanged={onFuelChanged} />}
			<HintMessage
				hints={[
					{
						tip: 'Wussten Sie, dass...?',
						message:
							'Für Erdgas- oder Biomethan-Fahrzeuge fällt nur die halbe Maut an; Elektro-, Wasserstoff- und Hybridfahrzeuge (max. CO2-Ausstoß 50 g/km) können mithilfe eines Antrags sogar ganz von der Maut befreit werden.',
						link: {
							text: 'Zur Beantragung einer Mautbefreiung*',
							url: 'https://edalnice.cz/de/befreiung/',
						},
					},
				]}
				bottomText="* Durch Anklicken des Links werden Sie auf eine externe Internetseite weitergeleitet, für deren Inhalt der jeweilige Seitenbetreiber verantwortlich ist."
			/>
		</>
	);

	const renderSchweizHintMessage = () => (
		<>
			{isSchweizPkwSelected && (
				<HintMessage
					className="mt-4 mb-2 transition-all duration-300 animate-fadeSlideIn"
					hints={getHintContent('Schweiz PKW', [], currentLabels?.hintMessages)}
				/>
			)}
			{isSchweizMotorradSelected && (
				<HintMessage
					className="mt-4 mb-2 transition-all duration-300 animate-fadeSlideIn"
					hints={getHintContent('Schweiz Motorrad', [], currentLabels?.hintMessages)}
				/>
			)}
		</>
	);

	return (
		<Wrapper
			animate={!withFuelOptions}
			animateFirstStep={withFuelOptions}
			paddingTop={`${supplierHasMultipleProducts ? 'large' : 'small'}`}>
			<Head
				headline={
					currentLabels?.headlineProductSelection?.replace(
						'{country}',
						getCountryName(supplierId, currentLabels?.oneCartCountries),
					) || 'Was benötigen Sie?'
				}
				description={supplierId === 3 ? currentLabels?.descriptionProductSelectionAut : undefined}
				helpContent={helpContent}
				trackingElement={`Produktauswahl ${getCountryName(supplierId, currentLabels?.oneCartCountries) || ''}`}
			/>
			<ContentWrapper className="flex flex-col">
				<VignetteRadioButtons
					configure
					hasError={hasError}
					hasFuelError={hasDifferentFuel && fuel === -1}
					selectedFuelId={fuel}
					supplierId={supplierId}
					teaserFuelId={teaserFuelPrice}
					defaultVignetteId={defaultVignetteIdSelector()}
					updateValidatedStepsMap={updateValidatedStepsMap}
					onVignetteChanged={onVignetteChanged}
				/>
				{withFuelOptions && renderFuelOptions()}
				{supplierId === 14 && renderSchweizHintMessage()}
				{supplierId === 3 && checkHintContent('productSelection', currentLabels?.hintMessages) && (
					<HintMessage
						className="mt-4"
						hints={getHintContent('productSelection', [], currentLabels?.hintMessages)}
					/>
				)}
				{supplierId === 3 && (
					<>
						<IconButton
							className="cursor-pointer w-fit inline-flex self-center gap-2 justify-center border-none text-base font-bold items-center mt-4 focus-visible:outline-primary hover:text-black"
							tracking={{
								info_click: {
									label: currentLabels?.oneCartTollSectionInfo || 'Produktauswahl Österreich',
									element: 'Länderauswahl',
									category: 'Content',
								},
							}}
							onClick={() => setDrawerIsOpen(true)}>
							<ArrowRight fill="currentColor" />
							{currentLabels?.oneCartTollSectionInfo || 'Wo benötige ich eine Streckenmaut?'}
						</IconButton>
						<Drawer
							paddingX="large"
							title={getDrawerTitle('productSelection', currentLabels?.helpDrawers)}
							isOpen={drawerIsOpen}
							setIsOpen={setDrawerIsOpen}>
							<ContentfulDrawerContent id={'productSelection'} />
						</Drawer>
					</>
				)}
			</ContentWrapper>
		</Wrapper>
	);
};

export const ProductSelection = withTracking(SelectionComponent);
